import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LightBulbIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSessionQuestions } from '../../contexts/SessionQuestionsContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Appointment, Case, MeetingNote } from '../../types';
import { formatNextAppointmentChipLabel, formatTimeAgo } from './dashboardUtils';
import {
  buildCaseCounselingTrail,
  parseSessionReportDoc,
  SessionReportRecord,
  toRoadSessionNumber,
} from '../SessionReports/sessionReportsUtils';
import CaseCounselingTrailView from '../SessionReports/CaseCounselingTrail';
import {
  parseMeetingNoteDoc,
  sessionLabel,
  tabloulLayerLabel,
  TABLOU_LAYER_KEYS,
} from '../MeetingNotes/meetingNotesUtils';
import { t } from '../../utils/translations';
import { dialogContentScrollSx, dialogPaperScrollSx } from '../common/dialogLayout';
import SlideUpTransition from '../common/SlideUpTransition';
import Button from '../ui/Button';

const EXPAND_CHAR_THRESHOLD = 220;

function ExpandableText({
  text,
  threshold = EXPAND_CHAR_THRESHOLD,
}: {
  text: string;
  threshold?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const needsExpand = text.trim().length > threshold;

  useEffect(() => {
    setExpanded(false);
  }, [text]);

  return (
    <div>
      <p
        className={`text-sm text-slate-800 whitespace-pre-wrap break-words ${
          needsExpand && !expanded ? 'line-clamp-4' : ''
        }`}
      >
        {text}
      </p>
      {needsExpand && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-semibold text-brand-600 transition hover:text-brand-700"
        >
          {expanded ? t.common.showLess : t.common.showMore}
        </button>
      )}
    </div>
  );
}

interface PrepareSessionDialogProps {
  open: boolean;
  caseItem: Case | null;
  appointment?: Appointment | null;
  sessionNumber?: number | null;
  reportCount: number;
  onClose: () => void;
  /** Clear parent state after close animation so the dialog does not shrink. */
  onExited?: () => void;
  onAddReport: (caseItem: Case) => void;
}

const PrepareSessionDialog: React.FC<PrepareSessionDialogProps> = ({
  open,
  caseItem,
  appointment,
  sessionNumber,
  reportCount,
  onClose,
  onExited,
  onAddReport,
}) => {
  const { openSessionQuestions, openTablou, dismissTablouAsk } = useSessionQuestions();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reports, setReports] = useState<SessionReportRecord[]>([]);
  const [lastNote, setLastNote] = useState<MeetingNote | null>(null);
  const [notes, setNotes] = useState<MeetingNote[]>([]);
  /** The session whose ask was just declined here — a session number, not a boolean, since
   * browsing can move on to a different session whose own ask (if any) is untouched. */
  const [skippedTablouSession, setSkippedTablouSession] = useState<number | null>(null);
  /** null = the actual last reported session (the default); set once a chip in "Sesiuni" is
   * picked. */
  const [selectedSession, setSelectedSession] = useState<number | null>(null);

  const caseId = caseItem?.id ?? null;

  useLayoutEffect(() => {
    if (open && caseId) setLoading(true);
  }, [open, caseId]);

  useEffect(() => {
    if (!open || !caseId) return;

    let cancelled = false;
    const load = async () => {
      try {
        setError('');
        const [reportsSnap, notesSnap] = await Promise.all([
          getDocs(query(collection(db, 'sessionReports'), where('caseId', '==', caseId))),
          getDocs(query(collection(db, 'meetingNotes'), where('caseId', '==', caseId))),
        ]);
        if (cancelled) return;

        const parsedReports: SessionReportRecord[] = [];
        reportsSnap.forEach((docSnap) => {
          parsedReports.push(parseSessionReportDoc(docSnap.id, docSnap.data()));
        });
        parsedReports.sort((a, b) => {
          if (a.sessionNumber !== b.sessionNumber) return a.sessionNumber - b.sessionNumber;
          return a.createdAt.getTime() - b.createdAt.getTime();
        });
        setReports(parsedReports);

        let newest: MeetingNote | null = null;
        const parsedNotes: MeetingNote[] = [];
        notesSnap.forEach((docSnap) => {
          const note = parseMeetingNoteDoc(docSnap.id, docSnap.data());
          parsedNotes.push(note);
          // Tablou notes are skipped here: the last session's is already rendered in full above,
          // and an older one would only show the same layers again as raw text.
          if (note.tablou) return;
          if (!newest || note.updatedAt.getTime() > newest.updatedAt.getTime()) {
            newest = note;
          }
        });
        setLastNote(newest);
        // The tablou is already in here — the briefing reads every note of the case anyway, so
        // showing it costs nothing on top.
        setNotes(parsedNotes);
      } catch (err) {
        console.error('Failed to load prepare briefing:', err);
        if (!cancelled) setError(t.dashboard.prepareSession.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, caseId]);

  const handleExited = () => {
    setReports([]);
    setLastNote(null);
    setNotes([]);
    setSkippedTablouSession(null);
    setSelectedSession(null);
    setError('');
    setLoading(false);
    onExited?.();
  };

  const lastReport = reports.length > 0 ? reports[reports.length - 1] : null;
  const allSessionNumbers = reports.map((r) => r.sessionNumber);
  const effectiveSession = selectedSession ?? lastReport?.sessionNumber ?? null;
  const selectedReport = reports.find((r) => r.sessionNumber === effectiveSession) ?? null;
  const selectedTablou =
    selectedReport == null
      ? null
      : notes.find((note) => note.sessionNumber === selectedReport.sessionNumber && note.tablou)
          ?.tablou ?? null;
  const isLatestSelected = selectedReport !== null && selectedReport.sessionNumber === lastReport?.sessionNumber;
  /**
   * Offered only while the case still says THIS session's tablou is outstanding — the same field
   * the dashboard and case-card asks read, so answering here (writing it or declining it) settles
   * it everywhere and on every device, rather than only in this browser.
   */
  const canOfferTablouAsk =
    !loading &&
    !error &&
    selectedReport !== null &&
    selectedTablou === null &&
    skippedTablouSession !== selectedReport.sessionNumber &&
    caseItem?.pendingClarifySession === selectedReport.sessionNumber;
  /** A tablou can always be added, even for a session nothing is actively asking about — just
   * without a "Sari peste", since there is no active ask here to decline. */
  const canAddTabloulQuietly =
    !loading && !error && selectedReport !== null && selectedTablou === null && !canOfferTablouAsk;
  const counselingTrail = buildCaseCounselingTrail(reports);
  const hasCounselingTrail =
    counselingTrail.themes.length > 0 || counselingTrail.passages.length > 0;
  const resolvedSession =
    typeof sessionNumber === 'number'
      ? sessionNumber
      : typeof appointment?.sessionNumber === 'number'
        ? appointment.sessionNumber
        : reportCount;

  const whenLabel = appointment
    ? formatNextAppointmentChipLabel(appointment)
    : t.dashboard.prepareSession.noAppointment;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      TransitionComponent={SlideUpTransition}
      TransitionProps={{ onExited: handleExited }}
      PaperProps={{ sx: dialogPaperScrollSx(fullScreen) }}
    >
      <DialogTitle className="flex shrink-0 items-start justify-between gap-3 !pr-3">
        <div className="min-w-0">
          <p className="text-base font-semibold text-slate-900">
            {t.dashboard.prepareSession.title.replace('{n}', String(resolvedSession))}
          </p>
          {caseItem && (
            <p className="mt-0.5 text-sm font-normal text-slate-500">
              {caseItem.counseledName} · {whenLabel}
            </p>
          )}
        </div>
        <IconButton aria-label={t.common.close} onClick={onClose} size="small" edge="end">
          <XMarkIcon className="h-5 w-5" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        className="!px-5 !py-4"
        sx={{
          ...dialogContentScrollSx(fullScreen),
          ...(!fullScreen ? { minHeight: '22rem' } : {}),
        }}
      >
        {!caseItem ? null : (
          <div className="divide-y divide-slate-100">
            {!loading && !error && hasCounselingTrail && (
              <section className="pb-5">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t.dashboard.prepareSession.counselingTrail}
                </h3>
                <div className="mt-1.5">
                  <CaseCounselingTrailView
                    reports={reports}
                    variant="full"
                    selectedSessionNumber={effectiveSession}
                    onSelectSession={setSelectedSession}
                  />
                </div>
              </section>
            )}

            <section
              className={!loading && !error && hasCounselingTrail ? 'py-5' : 'pb-5'}
            >
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {isLatestSelected || selectedReport === null
                  ? t.dashboard.prepareSession.lastSession
                  : t.dashboard.prepareSession.sessionHeading.replace(
                      '{n}',
                      String(toRoadSessionNumber(selectedReport.sessionNumber, allSessionNumbers))
                    )}
              </h3>
              {error ? (
                <p className="mt-1.5 text-sm text-red-600">{error}</p>
              ) : loading ? (
                <div className="mt-3 flex justify-center py-4">
                  <CircularProgress size={22} />
                </div>
              ) : selectedReport ? (
                <div className="mt-1.5 space-y-1.5 text-sm text-slate-800">
                  {selectedTablou && (
                    // Without a tablou the theme is already the first line of the report fallback
                    // below — shown here on its own only where that fallback is not, so it is
                    // never printed twice.
                    <p>
                      <span className="font-semibold text-slate-500">
                        {t.sessionReports.theme}:{' '}
                      </span>
                      {selectedReport.mainTheme}
                    </p>
                  )}

                  {canOfferTablouAsk && (
                    /**
                     * The one place left to write a tablou that was never started: by now the ask
                     * on the dashboard and the case card has gone quiet, and without an existing
                     * note there is no badge to reopen either.
                     */
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="min-w-0 flex-1 text-sm text-slate-600">
                        {t.dashboard.prepareSession.tabloulMissing.replace(
                          '{n}',
                          String(
                            toRoadSessionNumber(selectedReport.sessionNumber, allSessionNumbers)
                          )
                        )}
                      </p>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            onClose();
                            openTablou({
                              caseId: selectedReport.caseId,
                              sessionNumber: selectedReport.sessionNumber,
                            });
                          }}
                        >
                          {t.dashboard.prepareSession.tabloulFill}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSkippedTablouSession(selectedReport.sessionNumber);
                            dismissTablouAsk({
                              caseId: selectedReport.caseId,
                              sessionNumber: selectedReport.sessionNumber,
                            });
                          }}
                        >
                          {t.dashboard.prepareSession.tabloulSkip}
                        </Button>
                      </div>
                    </div>
                  )}

                  {selectedTablou &&
                    TABLOU_LAYER_KEYS.map((key) => {
                      const value = (selectedTablou[key] ?? '').trim();
                      if (!value) return null;
                      return (
                        <p key={key}>
                          <span className="font-semibold text-slate-500">
                            {tabloulLayerLabel(key)}:{' '}
                          </span>
                          {value}
                        </p>
                      );
                    })}

                  {!selectedTablou && (
                    /**
                     * Without a tablou the report is what there is — thin, but better than a
                     * section holding the homework alone. It is also what every case looks like
                     * until it has been through one session with the tablou in place.
                     */
                    <>
                      <p>
                        <span className="font-semibold text-slate-500">
                          {t.sessionReports.theme}:{' '}
                        </span>
                        {selectedReport.mainTheme}
                      </p>
                      <p>
                        <span className="font-semibold text-slate-500">
                          {t.sessionReports.wizard.personResponse}:{' '}
                        </span>
                        {selectedReport.personResponse}
                      </p>
                      {selectedReport.progressNoted && (
                        <p>
                          <span className="font-semibold text-slate-500">
                            {t.sessionReports.wizard.progress}:{' '}
                          </span>
                          {selectedReport.progressObserved
                            ? `${
                                selectedReport.progressObserved === 'yes'
                                  ? t.common.yes
                                  : selectedReport.progressObserved === 'partial'
                                    ? t.sessionReports.wizard.partial
                                    : t.common.no
                              } — ${selectedReport.progressNoted}`
                            : selectedReport.progressNoted}
                        </p>
                      )}
                    </>
                  )}

                  {canAddTabloulQuietly && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        openTablou({
                          caseId: selectedReport.caseId,
                          sessionNumber: selectedReport.sessionNumber,
                        });
                      }}
                      className="text-xs font-semibold text-brand-600 transition hover:text-brand-700"
                    >
                      {t.dashboard.prepareSession.tabloulAdd}
                    </button>
                  )}

                  {selectedReport.nextCommitments === 'yes' &&
                    selectedReport.nextCommitmentsDetails && (
                      /**
                       * Lifted out of the plain run of report fields: this is the one line the
                       * counselor has to act on at the next meeting — the report asks whether it
                       * was kept — so it carries the brand callout the rest of the briefing does
                       * not.
                       */
                      <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50 p-3">
                        <div className="flex items-baseline gap-2">
                          <p className="text-sm font-semibold text-brand-800">
                            {t.dashboard.prepareSession.openStep}
                          </p>
                          <p className="text-xs text-brand-700">
                            {t.dashboard.prepareSession.openStepToVerify}
                          </p>
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-slate-800">
                          {selectedReport.nextCommitmentsDetails}
                        </p>
                        {(selectedReport.nextStepType || selectedReport.nextStepPassage) && (
                          <p className="mt-1.5 text-xs text-brand-800">
                            {[
                              selectedReport.nextStepType
                                ? t.sessionReports.wizard.nextStepTypes[
                                    selectedReport.nextStepType
                                  ]
                                : null,
                              selectedReport.nextStepPassage
                                ? `${t.dashboard.prepareSession.openStepPassage}: ${selectedReport.nextStepPassage}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        )}
                      </div>
                    )}
                </div>
              ) : (
                <p className="mt-1.5 text-sm text-slate-500">
                  {t.dashboard.prepareSession.noLastSession}
                </p>
              )}
            </section>

            <section className="py-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t.dashboard.prepareSession.history}
              </h3>
              {error ? null : loading ? (
                <div className="mt-3 flex justify-center py-4">
                  <CircularProgress size={22} />
                </div>
              ) : lastNote ? (
                <div className="mt-1.5 space-y-1">
                  <p className="text-xs text-slate-500">
                    {sessionLabel(lastNote.sessionNumber)}
                    {' · '}
                    {formatTimeAgo(lastNote.updatedAt)}
                    {lastNote.createdByName ? ` · ${lastNote.createdByName}` : ''}
                  </p>
                  <ExpandableText text={lastNote.content} />
                </div>
              ) : (
                <p className="mt-1.5 text-sm text-slate-500">
                  {t.dashboard.prepareSession.noHistory}
                </p>
              )}
            </section>

            <section className="pt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t.dashboard.prepareSession.initialProblem}
              </h3>
              <div className="mt-1.5">
                <ExpandableText
                  text={
                    caseItem.description?.trim() || t.dashboard.prepareSession.noDescription
                  }
                />
              </div>
            </section>

          </div>
        )}
      </DialogContent>

      <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 px-5 py-3">
        <Button onClick={onClose}>{t.common.close}</Button>
        <Button
          variant="primary"
          icon={LightBulbIcon}
          onClick={() => {
            onClose();
            openSessionQuestions(caseItem?.id);
          }}
        >
          {t.dashboard.todayFocus.openMemento}
        </Button>
      </div>
    </Dialog>
  );
};

export default PrepareSessionDialog;
